
import React, { useState } from "react";
import { Info, AlertCircle, CheckCircle2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface AIWordDisplayProps {
  word: string;
  explanation?: string;
  isSubmitting: boolean;
  isCurrentRound: boolean;
  isRevealed: boolean;
}

export const AIWordDisplay: React.FC<AIWordDisplayProps> = ({
  word,
  explanation,
  isSubmitting,
  isCurrentRound,
  isRevealed
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const handleInfoClick = () => {
    setTooltipOpen(!tooltipOpen);
  };

  // Animated ellipsis component
  const ThinkingEllipsis = () => <div className="inline-flex items-center justify-center bg-secondary rounded-full px-3 py-1.5">
      <div className="flex space-x-1">
        <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-[pulse_1.2s_ease-in-out_0s_infinite]"></div>
        <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-[pulse_1.2s_ease-in-out_0.4s_infinite]"></div>
        <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-[pulse_1.2s_ease-in-out_0.8s_infinite]"></div>
      </div>
    </div>;

  // If words are being processed (when user submitted second), show the typing indicator
  if (isSubmitting && isCurrentRound && word !== "Word chosen") {
    return <ThinkingEllipsis />;
  }

  // Check if the explanation contains an error message (fallback word)
  const isFallbackWord = explanation && explanation.includes("fallback word");
  
  if (isRevealed) {
    return <div className="w-full h-full relative flex items-center justify-center">
        <span>{word.toLowerCase()}</span>
        {explanation && <TooltipProvider>
            <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
              <TooltipTrigger asChild>
                <button className="inline-flex ml-2" aria-label="View explanation" onClick={handleInfoClick}>
                  {isFallbackWord ? <AlertCircle className="h-3.5 w-3.5 text-amber-500 hover:text-amber-600 cursor-pointer transition-colors" /> : <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground cursor-pointer transition-colors" />}
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">{explanation}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>}
      </div>;
  }
  
  if (word === "Word chosen") {
    return (
      <div className="w-full h-full relative flex items-center justify-center">
        <CheckCircle2 className="h-5 w-5 text-green-500" />
      </div>
    );
  }

  // Default case - always show the typing indicator instead of "thinking..."
  return <ThinkingEllipsis />;
};
